import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Editor } from "@tiptap/react";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  FileSignature,
  History,
  Info,
  Loader2,
  Save,
  Send,
  ShieldCheck,
  Trash2,
  XCircle,
  ClipboardCheck,
  FileText,
} from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchSelect } from "@/components/ui/search-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn, formatINR } from "@/lib/utils";
import { ProposalStatusBadge, PROPOSAL_STATUS_LABELS } from "@/components/proposals/ProposalStatusBadge";
import { SectionsEditor, sectionProblem, toApiSections, toEditableSections, type EditableSection } from "@/components/proposals/SectionsEditor";
import { paymentAmounts, proposalTotals } from "@/lib/proposalTotals";
import { useConfirm } from "@/context/ConfirmContext";
import { EstimateEditor, estimateProblem, estimateTotal, toApiLines, toEditableLines, type EditableEstimateLine } from "@/components/proposals/EstimateEditor";
import { MergeFieldPanel } from "@/components/proposals/MergeFieldPanel";
import { ProposalDocument } from "@/components/proposals/ProposalDocument";
import { UploadedPdfViewer } from "@/components/proposals/UploadedPdfViewer";
import { SendProposalDialog } from "@/components/proposals/SendProposalDialog";
import { ProposalActivityCard } from "@/components/proposals/ProposalActivityCard";
import { ProposalDecisionCard } from "@/components/proposals/ProposalDecisionCard";
import { RecordDecisionDialog } from "@/components/proposals/RecordDecisionDialog";
import type { ProposalRecord, ProposalRender } from "@/types/proposal";
import { dateLocale, withClock } from "@/lib/dateTime";

const fmtDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })) : "-";

export function ProposalEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const confirm = useConfirm();
  const canUpdate = hasPermission("proposal.update");

  const [p, setP] = useState<ProposalRecord | null>(null);
  useRecordCrumb(p ? p.title || p.display_number : undefined);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState("");
  const [contactId, setContactId] = useState("");
  const [contactLabel, setContactLabel] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [sections, setSections] = useState<EditableSection[]>([]);
  const [lines, setLines] = useState<EditableEstimateLine[]>([]);
  const [gstRate, setGstRate] = useState<number | null>(null);
  const [includesGst, setIncludesGst] = useState(false);
  // Uploaded proposals: the typed total (before GST); there are no price lines
  const [uploadAmount, setUploadAmount] = useState("");
  const [dirty, setDirty] = useState(false);
  const activeEditor = useRef<Editor | null>(null);

  const [preview, setPreview] = useState<ProposalRender | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [reviewDialog, setReviewDialog] = useState<"approve" | "reject" | null>(null);
  const [comment, setComment] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [activityKey, setActivityKey] = useState(0);
  // The section being edited: the others fold away so the page reads as one editor, with the outline to jump around
  const [openSection, setOpenSection] = useState<string | null>(null);
  useEffect(() => {
    if (!openSection && sections.length) setOpenSection((sections.find((x) => x.type !== "cover") || sections[0])!.key);
  }, [sections, openSection]);
  // A sent proposal is shown as the customer sees it (its frozen copy)
  const [sentDoc, setSentDoc] = useState<ProposalRender | null>(null);
  const [decisionOpen, setDecisionOpen] = useState(false);

  const apply = useCallback((rec: ProposalRecord) => {
    setP(rec);
    setTitle(rec.title);
    setContactId(rec.contact_id || "");
    setContactLabel(rec.contact?.name || "");
    setValidUntil(rec.valid_until?.slice(0, 10) || "");
    setSections(toEditableSections(rec.sections));
    setLines(toEditableLines(rec.estimate_lines));
    setGstRate(rec.gst_rate === null || rec.gst_rate === undefined ? null : Number(rec.gst_rate));
    setIncludesGst(!!rec.amounts_include_gst);
    setUploadAmount(String(Number(rec.estimate_total || 0)));
    setDirty(false);
    activeEditor.current = null;
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      apply(unwrapObject<ProposalRecord>(await api.get(`/api/Proposal/ById/${id}`)));
    } catch (err: any) {
      error("Couldn't open the proposal", err.message || "Proposal not found");
      navigate("/proposals");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const editable = !!p?.editable && canUpdate;
  const showSent = !!p?.sent_at && !p.editable;
  useEffect(() => {
    if (!showSent || !p) {
      setSentDoc(null);
      return;
    }
    api
      .get(`/api/Proposal/Preview/${p.id}`)
      .then((res) => setSentDoc(unwrapObject<ProposalRender>(res)))
      .catch(() => setSentDoc(null));
  }, [showSent, p?.id, p?.proposal_status]);
  const markDirty = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };

  // Warn before leaving with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const save = async (silent = false, extra: Record<string, any> = {}): Promise<boolean> => {
    if (!p) return false;
    const isUploaded = p.source === "UPLOADED";
    const problem = !title.trim() ? "Give the proposal a title" : isUploaded ? null : sectionProblem(sections) || estimateProblem(lines);
    if (problem) {
      error("Can't save yet", problem);
      return false;
    }
    try {
      const res = await api.put(
        `/api/Proposal/Update/${p.id}`,
        isUploaded
          ? { title: title.trim(), contact_id: contactId || null, valid_until: validUntil || undefined, gst_rate: gstRate, amount: Number(uploadAmount) || 0, ...extra }
          : {
              title: title.trim(),
              contact_id: contactId || null,
              valid_until: validUntil || undefined,
              sections: toApiSections(sections),
              estimate_lines: toApiLines(lines),
              gst_rate: gstRate,
              amounts_include_gst: includesGst,
            }
      );
      const rec = unwrapObject<ProposalRecord>(res);
      const backToDraft = p.proposal_status === "APPROVED" && rec.proposal_status === "DRAFT";
      apply(rec);
      if (!silent) success("Saved", backToDraft ? "Changes saved. It was approved before, so it needs a new review." : "Proposal saved");
      return true;
    } catch (err: any) {
      error("Save failed", err.message || "Could not save the proposal");
      return false;
    }
  };

  const saveIfDirty = async () => (dirty ? save(true) : true);

  // Ctrl/Cmd+S saves
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (editable && dirty && !busy) {
          setBusy(true);
          saveRef.current().finally(() => setBusy(false));
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editable, dirty, busy]);

  const act = async (fn: () => Promise<any>, okTitle: string, okMsg: string) => {
    setBusy(true);
    try {
      const res = await fn();
      success(okTitle, okMsg);
      return res;
    } catch (err: any) {
      error("Action failed", err.message || "Something went wrong");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async () => {
    if (!p) return;
    setBusy(true);
    try {
      if (!(await saveIfDirty())) return;
      setPreview(unwrapObject<ProposalRender>(await api.get(`/api/Proposal/Preview/${p.id}`)));
      setPreviewOpen(true);
    } catch (err: any) {
      error("Preview failed", err.message || "Could not load the preview");
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async () => {
    if (!p) return;
    setBusy(true);
    const ok = await saveIfDirty();
    setBusy(false);
    if (ok) window.open(`/api/Proposal/Pdf/${p.id}`, "_blank");
  };

  // [Bracketed] template text still to be replaced, counted on what is on screen
  const placeholders = (() => {
    const text = [title, ...sections.flatMap((s) => [s.title, s.html || ""])].join(" ");
    return (text.match(/\[[^\[\]\n<>]{3,}\]/g) || []).length;
  })();
  const okToGo = async (what: string) =>
    placeholders === 0 ||
    (await confirm({
      title: `${placeholders} [bracketed] placeholder${placeholders === 1 ? "" : "s"} still to replace`,
      message: "The template's example text in [square brackets] would go to the customer as it is. Replace it first, or carry on if it's meant to stay.",
      confirmLabel: `${what} anyway`,
    }));

  const submit = async () => {
    if (!p) return;
    if (!(await okToGo("Submit"))) return;
    setBusy(true);
    const ok = await saveIfDirty();
    setBusy(false);
    if (!ok) return;
    const res = await act(() => api.post(`/api/Proposal/Submit/${p.id}`), "Submitted", "Your Sales Head has been asked to review it");
    if (res) apply(unwrapObject<ProposalRecord>(res));
  };

  const review = async () => {
    if (!p || !reviewDialog) return;
    if (reviewDialog === "reject" && !comment.trim()) {
      error("Say what needs changing", "Add a comment for the rep");
      return;
    }
    const approve = reviewDialog === "approve";
    const res = await act(
      () => api.post(`/api/Proposal/${approve ? "Approve" : "Reject"}/${p.id}`, { comment: comment.trim() || undefined }),
      approve ? "Approved" : "Sent back",
      approve ? "The rep can send it to the customer now" : "The rep has been told what to change"
    );
    if (res) {
      apply(unwrapObject<ProposalRecord>(res));
      setReviewDialog(null);
    }
  };

  const newVersion = async () => {
    if (!p) return;
    const res = await act(() => api.post(`/api/Proposal/NewVersion/${p.id}`), "New version", "A new draft was made from this proposal; the sent one is kept as a record");
    if (res) navigate(`/proposals/${unwrapObject<ProposalRecord>(res).id}`);
  };

  const createQuotation = async () => {
    if (!p) return;
    const res = await act(
      () => api.post(`/api/Proposal/CreateQuotation/${p.id}`),
      "Quotation started",
      "The proposal's price lines were copied into a draft quotation. Add GST details and send it for approval."
    );
    const q = res ? unwrapObject<{ quotation_id: string }>(res) : null;
    if (q?.quotation_id) navigate(`/quotes/${q.quotation_id}`);
  };

  const openSend = async () => {
    if (!(await okToGo("Send"))) return;
    setBusy(true);
    const ok = await saveIfDirty();
    setBusy(false);
    if (ok) setSendOpen(true);
  };

  const remove = async () => {
    if (!p) return;
    const res = await act(() => api.delete(`/api/Proposal/Delete/${p.id}`), "Deleted", `${p.display_number} deleted`);
    setDeleteOpen(false);
    if (res) {
      setDirty(false);
      navigate(`/deals/${p.deal_id}`);
    }
  };

  if (loading || !p) return <FormSkeleton />;

  const status = p.proposal_status;
  const latest = p.versions[0];
  const isLatest = latest?.id === p.id;
  const readyToSend = status === "APPROVED" || (status === "DRAFT" && !p.requires_review);
  const uploaded = p.source === "UPLOADED";
  const typedTotal = uploaded ? (editable ? Number(uploadAmount) || 0 : Number(p.estimate_total)) : editable ? estimateTotal(lines) : Number(p.estimate_total);
  // With GST shown, the header total is what the customer pays, GST included
  const figures = proposalTotals(typedTotal, editable ? gstRate : p.gst_rate, editable ? includesGst : p.amounts_include_gst);
  const total = figures.total;

  return (
    <div className="space-y-4 pb-10">
      {/* Header */}
      <section aria-label="Proposal summary" className="rounded-lg border border-border bg-card px-4 py-3.5">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <Link to={`/deals/${p.deal_id}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="size-3.5" /> {p.deal.name}
          </Link>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-[19px] font-semibold leading-tight tracking-tight">{p.title || p.display_number}</h1>
            <ProposalStatusBadge status={status} />
            {dirty && <span className="text-xs text-warning">Unsaved changes</span>}
          </div>
          <p className="text-[13px] text-muted-foreground">
            <span className="font-mono text-xs">{p.display_number}</span>
            {p.account?.name && (
              <>
                {" · "}
                <span className="font-medium text-foreground">{p.account.name}</span>
              </>
            )}
            {p.deal.name && ` · ${p.deal.name}`}
            {p.sent_at && ` · sent ${fmtDate(p.sent_at)}`}
            {p.valid_until && ` · valid until ${fmtDate(p.valid_until)}`}
            {p.owner && ` · ${p.owner.name}`}
            {p.template && ` · from "${p.template.name}"`}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!uploaded && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={openPreview} disabled={busy}>
              <Eye className="size-4" /> Preview
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={openPdf} disabled={busy}>
            <Download className="size-4" /> {status === "ACCEPTED" ? "Signed PDF" : "PDF"}
          </Button>
          {p.can_delete && canUpdate && (
            <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={() => setDeleteOpen(true)} disabled={busy}>
              <Trash2 className="size-4" /> Delete
            </Button>
          )}
          {editable && (
            <Button variant={readyToSend && !dirty ? "outline" : "default"} size="sm" className="gap-1.5" onClick={() => (setBusy(true), save().finally(() => setBusy(false)))} disabled={busy || !dirty}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save
            </Button>
          )}
          {editable && placeholders > 0 && (
        <div role="status" className="flex gap-2 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>{placeholders} [bracketed] placeholder{placeholders === 1 ? "" : "s"} from the template still need replacing before this goes to the customer.</div>
        </div>
      )}
      {status === "DRAFT" && p.requires_review && canUpdate && (
            <Button size="sm" className="gap-1.5" onClick={submit} disabled={busy}>
              <ShieldCheck className="size-4" /> Submit for approval
            </Button>
          )}
          {p.can_review && (
            <>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => (setComment(""), setReviewDialog("reject"))} disabled={busy}>
                <XCircle className="size-4" /> Send back
              </Button>
              <Button size="sm" className="gap-1.5" onClick={() => (setComment(""), setReviewDialog("approve"))} disabled={busy}>
                <CheckCircle2 className="size-4" /> Approve
              </Button>
            </>
          )}
          {p.can_new_version && canUpdate && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={newVersion} disabled={busy}>
              <Copy className="size-4" /> New version
            </Button>
          )}
          {p.can_record_decision && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDecisionOpen(true)} disabled={busy}>
              <ClipboardCheck className="size-4" /> Record decision
            </Button>
          )}
          {p.quotation && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/quotes/${p.quotation!.id}`)}>
              <FileText className="size-4" /> Quotation {p.quotation.display_number}
            </Button>
          )}
          {p.can_create_quotation && (
            <Button size="sm" className="gap-1.5" onClick={createQuotation} disabled={busy}>
              <FileText className="size-4" /> Create quotation
            </Button>
          )}
          {p.can_send && (
            <Button size="sm" className="gap-1.5" onClick={openSend} disabled={busy}>
              <Send className="size-4" /> {status === "SENT" || status === "VIEWED" ? "Send again" : "Send"}
            </Button>
          )}
        </div>
      </div>
      </section>

      {/* Banners */}
      {status === "DRAFT" && p.review_comment && p.reviewed_by && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm flex gap-2">
          <AlertTriangle className="size-4 text-warning shrink-0 mt-0.5" />
          <div>
            <strong>Sent back by {p.reviewed_by.name}:</strong> {p.review_comment}
          </div>
        </div>
      )}
      {status === "DRAFT" && p.requires_review && canUpdate && (
        <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm flex gap-2">
          <ShieldCheck className="size-4 text-muted-foreground shrink-0 mt-0.5" />
          <div>This proposal is approved by a Sales Head before it goes to the customer. Submit it for approval when it's ready; changing it after approval needs approving again.</div>
        </div>
      )}
      {status === "IN_REVIEW" && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm flex gap-2">
          <ShieldCheck className="size-4 text-warning shrink-0 mt-0.5" />
          <div>
            Waiting for {p.pending_reviewer?.name || "a reviewer"}'s approval since {fmtDateTime(p.submitted_at)}.{" "}
            {p.can_review ? "Read it through, then approve it or send it back with what to change." : "It can't be edited or sent until it's approved."}
          </div>
        </div>
      )}
      {status === "APPROVED" && (
        <div className="rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm flex gap-2">
          <CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" />
          <div>
            Approved by {p.reviewed_by?.name || "a reviewer"}
            {p.review_comment ? `: "${p.review_comment}"` : ""}. Ready to send; editing it again needs a new review.
          </div>
        </div>
      )}
      {(status === "SENT" || status === "VIEWED") && !p.is_expired && (
        <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm flex gap-2">
          <Send className="size-4 text-primary shrink-0 mt-0.5" />
          <div>
            Sent {fmtDateTime(p.sent_at)}.{" "}
            {status === "VIEWED"
              ? `Opened ${p.view_count}×, last ${fmtDateTime(p.last_viewed_at)}.`
              : "Not opened yet."}{" "}
            The customer sees it exactly as sent; to change it, make a new version. Valid until {fmtDate(p.valid_until)}.
          </div>
        </div>
      )}
      {status === "ACCEPTED" && p.decision && (
        <div className="rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm flex gap-2">
          <CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" />
          <div>
            Accepted by {p.decision.signer_name || "the customer"} on {fmtDateTime(p.decision.decided_at)}
            {p.decision.method === "RECORDED" ? " (recorded by hand)" : ""}.{" "}
            {p.quotation
              ? `Quotation ${p.quotation.display_number} was made from it.`
              : p.can_create_quotation
                ? "Next: create the formal quotation from its price lines."
                : ""}
          </div>
        </div>
      )}
      {status === "DECLINED" && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm flex gap-2">
          <XCircle className="size-4 text-destructive shrink-0 mt-0.5" />
          <div>
            Declined{p.decision?.signer_name ? ` by ${p.decision.signer_name}` : ""} on {fmtDateTime(p.declined_at)}
            {p.decline_reason ? `: "${p.decline_reason}"` : ""}. {p.can_new_version ? "Make a new version to send a revised offer." : ""}
          </div>
        </div>
      )}
      {(status === "EXPIRED" || ((status === "SENT" || status === "VIEWED") && p.is_expired)) && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm flex gap-2">
          <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />
          <div>Expired on {fmtDate(p.valid_until)}. The customer's link no longer opens it; make a new version to send it again.</div>
        </div>
      )}
      {editable && p.is_expired && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm flex gap-2">
          <AlertTriangle className="size-4 text-warning shrink-0 mt-0.5" />
          <div>The valid-until date has passed. Set a later date before sending.</div>
        </div>
      )}
      {status === "SUPERSEDED" && (
        <div className="rounded-xl border bg-muted/40 px-4 py-3 text-sm flex gap-2">
          <History className="size-4 text-muted-foreground shrink-0 mt-0.5" />
          <div>
            This version was replaced.{" "}
            {latest && !isLatest && (
              <Link to={`/proposals/${latest.id}`} className="text-primary hover:underline font-medium">
                Open the latest ({latest.display_number})
              </Link>
            )}
          </div>
        </div>
      )}
      {p.unknown_merge_fields.length > 0 && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm flex gap-2">
          <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />
          <div>
            Unknown merge field{p.unknown_merge_fields.length > 1 ? "s" : ""}:{" "}
            {p.unknown_merge_fields.map((f) => (
              <code key={f} className="mx-0.5 px-1 rounded bg-background text-xs">{`{{${f}}}`}</code>
            ))}
            . The customer would see {p.unknown_merge_fields.length > 1 ? "them" : "it"} as written; check the spelling against the merge fields list.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        {/* Section outline (wide screens, while editing) */}
        {!showSent && !uploaded && (
          <aside aria-label="Sections" className="hidden xl:sticky xl:top-4 xl:col-span-2 xl:block">
            <h2 className="mb-1.5 px-2 text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">Sections</h2>
            <nav className="flex flex-col gap-0.5">
              {sections.map((sec) => (
                <a key={sec.key} href={`#section-${sec.key}`} onClick={() => setOpenSection(sec.key)} className="flex h-8 items-center justify-between gap-2 rounded-md px-2 text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground">
                  <span className="truncate">{sec.title || "Untitled"}</span>
                  {{ cover: "Cover", estimate: "Table", acceptance: "Sign" }[sec.type as string] && (
                    <span className="rounded bg-muted px-1.5 text-[10.5px] font-medium">{{ cover: "Cover", estimate: "Table", acceptance: "Sign" }[sec.type as string]}</span>
                  )}
                </a>
              ))}
            </nav>
          </aside>
        )}

        {/* Main column */}
        {uploaded ? (
          <div className="xl:col-span-8 space-y-4">
            <div className="bg-card border border-border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-1 md:col-span-4">
                <Label className="text-xs">Title</Label>
                <Input value={title} disabled={!editable} maxLength={200} onChange={(e) => markDirty(setTitle)(e.target.value)} />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs">Addressed to</Label>
                {editable ? (
                  <SearchSelect
                    endpoint="/api/Contact/List"
                    extraParams={{ account_id: p.account_id || undefined }}
                    value={contactId}
                    selectedLabel={contactLabel}
                    getLabel={(row) => `${row.first_name || ""} ${row.last_name || ""}`.trim() + (row.email ? ` (${row.email})` : "")}
                    placeholder="Search the account's contacts"
                    allowEmpty
                    emptyLabel="-- Nobody in particular --"
                    onChange={(cid, row) => {
                      setContactId(cid);
                      setContactLabel(row ? `${row.first_name || ""} ${row.last_name || ""}`.trim() : "");
                      setDirty(true);
                    }}
                  />
                ) : (
                  <div className="text-sm h-9 flex items-center">{p.contact ? `${p.contact.name}${p.contact.email ? ` (${p.contact.email})` : ""}` : "-"}</div>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Valid until</Label>
                <Input type="date" value={validUntil} disabled={!editable} onChange={(e) => markDirty(setValidUntil)(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Amount before GST (₹)</Label>
                <Input type="number" min="0" step="0.01" value={uploadAmount} disabled={!editable} onChange={(e) => markDirty(setUploadAmount)(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">GST %</Label>
                <Input type="number" min="0" max="28" step="0.01" value={gstRate ?? ""} disabled={!editable} onChange={(e) => { setGstRate(e.target.value === "" ? null : Number(e.target.value)); setDirty(true); }} />
              </div>
            </div>
            <UploadedPdfViewer
              src={`/api/Proposal/Pdf/${p.id}?v=${p.file_sha256 || ""}`}
              fileName={p.file_name}
              fileSize={p.file_size}
              canReplace={editable}
              busy={busy}
              hint={editable ? "This PDF is exactly what the customer sees on their link and in the email." : undefined}
              onReplace={async (f) => {
                setBusy(true);
                try {
                  const fd = new FormData();
                  fd.append("deal_id", p.deal_id);
                  fd.append("file", f);
                  const up: any = await api.upload("/api/Upload/File", fd);
                  const stored = up?.AddtionalData || up?.data || up;
                  if (await save(true, { file_url: stored?.url, file_name: stored?.original_name || f.name })) success("PDF replaced", f.name);
                } catch (err: any) {
                  error("Couldn't replace the PDF", err.message || "Something went wrong");
                } finally {
                  setBusy(false);
                }
              }}
            />
          </div>
        ) : showSent ? (
          <div className="xl:col-span-8 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-bold">As sent to the customer</h3>
              <span className="text-xs text-muted-foreground">Addressed to {p.contact?.name || "-"}</span>
            </div>
            <div className="rounded-2xl bg-muted/50 p-3 sm:p-4">
              {sentDoc ? <ProposalDocument doc={sentDoc} /> : <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>}
            </div>
          </div>
        ) : (
        <div className="xl:col-span-6 space-y-4">
          <div className="bg-card border border-border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1 md:col-span-4">
              <Label className="text-xs">Title</Label>
              <Input value={title} disabled={!editable} maxLength={200} onChange={(e) => markDirty(setTitle)(e.target.value)} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Addressed to</Label>
              {editable ? (
                <SearchSelect
                  endpoint="/api/Contact/List"
                  extraParams={{ account_id: p.account_id || undefined }}
                  value={contactId}
                  selectedLabel={contactLabel}
                  getLabel={(row) => `${row.first_name || ""} ${row.last_name || ""}`.trim() + (row.email ? ` (${row.email})` : "")}
                  placeholder="Search the account's contacts"
                  allowEmpty
                  emptyLabel="-- Nobody in particular --"
                  onChange={(cid, row) => {
                    setContactId(cid);
                    setContactLabel(row ? `${row.first_name || ""} ${row.last_name || ""}`.trim() : "");
                    setDirty(true);
                  }}
                />
              ) : (
                <div className="text-sm h-9 flex items-center">{p.contact ? `${p.contact.name}${p.contact.email ? ` (${p.contact.email})` : ""}` : "-"}</div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Valid until</Label>
              <Input type="date" value={validUntil} disabled={!editable} onChange={(e) => markDirty(setValidUntil)(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{(editable ? gstRate : p.gst_rate) === null ? "Indicative total" : "Total incl. GST"}</Label>
              <div className="h-9 flex items-center text-sm font-bold">{formatINR(total)}</div>
            </div>
          </div>

          <SectionsEditor
            sections={sections}
            openKey={openSection}
            onChange={markDirty(setSections)}
            disabled={!editable}
            activeEditor={activeEditor}
            renderExtra={(s) =>
              s.type === "estimate" ? (
                <EstimateEditor
                  lines={lines}
                  onChange={markDirty(setLines)}
                  disabled={!editable}
                  gstRate={gstRate}
                  includesGst={includesGst}
                  onGstChange={(rate, inc) => {
                    setGstRate(rate);
                    setIncludesGst(rate === null ? false : inc);
                    setDirty(true);
                  }}
                />
              ) : s.type === "payment" && (s.rows || []).length ? (
                <div className="rounded-lg border bg-muted/30 px-3 py-2 text-xs">
                  <div className="mb-1 font-semibold text-muted-foreground">What the customer will see (from the total of {formatINR(total)})</div>
                  <ul className="space-y-0.5">
                    {paymentAmounts(s.rows || [], total).map((r, i) => (
                      <li key={i}>
                        <strong>{r.percent}% {r.label ? r.label.charAt(0).toLowerCase() + r.label.slice(1) : "…"}</strong> - {formatINR(r.amount)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null
            }
          />
        </div>
        )}

        {/* Side column */}
        <div className="xl:col-span-4 space-y-4 xl:sticky xl:top-4">
          {editable && <MergeFieldPanel fields={p.merge_fields} activeEditor={activeEditor} />}
          {p.decision && <ProposalDecisionCard d={p.decision} />}
          {p.sent_at && <ProposalActivityCard proposalId={p.id} canRevoke={hasPermission("proposal.send")} refreshKey={activityKey} canCopyLink={p.can_send} onLinkCreated={() => setActivityKey((k) => k + 1)} />}

          <div className="bg-card border border-border rounded-lg p-4 space-y-2.5 text-sm">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Info className="size-4 text-primary" /> Details
            </h3>
            <Row label="Deal">
              <Link to={`/deals/${p.deal_id}`} className="text-primary hover:underline">
                {p.deal.name}
              </Link>
              {p.deal.stage && <span className="text-muted-foreground"> · {p.deal.stage}</span>}
            </Row>
            <Row label="Customer">{p.account?.name || "-"}</Row>
            <Row label="Owner">{p.owner?.name || "-"}</Row>
            <Row label="Created">
              {fmtDateTime(p.created_on)}
              {p.created_by && p.created_by.id !== p.owner?.id && ` by ${p.created_by.name}`}
            </Row>
            {p.submitted_at && <Row label="Submitted">{fmtDateTime(p.submitted_at)}</Row>}
            {p.reviewed_at && p.reviewed_by && (
              <Row label="Reviewed">
                {fmtDateTime(p.reviewed_at)} by {p.reviewed_by.name}
              </Row>
            )}
            {p.sent_at && <Row label="Sent">{fmtDateTime(p.sent_at)}</Row>}
            {p.first_viewed_at && (
              <Row label="Viewed">
                {fmtDateTime(p.first_viewed_at)} · {p.view_count} view{p.view_count === 1 ? "" : "s"}
              </Row>
            )}
            {p.accepted_at && <Row label="Accepted">{fmtDateTime(p.accepted_at)}</Row>}
            {p.declined_at && (
              <Row label="Declined">
                {fmtDateTime(p.declined_at)}
                {p.decline_reason && `: ${p.decline_reason}`}
              </Row>
            )}
            <Row label="Valid until">{fmtDate(p.valid_until)}</Row>
          </div>

          {p.versions.length > 1 && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <h3 className="text-sm font-bold flex items-center gap-2 px-4 py-3 border-b">
                <History className="size-4 text-primary" /> Versions
              </h3>
              <ul className="divide-y">
                {p.versions.map((v) => (
                  <li key={v.id}>
                    <Link
                      to={`/proposals/${v.id}`}
                      className={cn("flex items-center justify-between gap-2 px-4 py-2 text-xs hover:bg-muted/50", v.id === p.id && "bg-primary/5")}
                    >
                      <span className="font-mono font-semibold">{v.display_number}</span>
                      <span className="text-muted-foreground">{fmtDate(v.sent_at || v.created_on)}</span>
                      <span>{PROPOSAL_STATUS_LABELS[v.proposal_status] || v.proposal_status}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto bg-muted">
          <DialogHeader>
            <DialogTitle>Customer preview</DialogTitle>
            <DialogDescription>What {preview?.customer.contact || "the customer"} will see, with the merge fields filled in.</DialogDescription>
          </DialogHeader>
          {preview && <ProposalDocument doc={preview} />}
        </DialogContent>
      </Dialog>

      {/* Approve / send back */}
      <Dialog open={!!reviewDialog} onOpenChange={(o) => !o && setReviewDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{reviewDialog === "approve" ? "Approve proposal" : "Send back for changes"}</DialogTitle>
            <DialogDescription>
              {reviewDialog === "approve"
                ? `${p.owner?.name || "The rep"} can then send ${p.display_number} to the customer.`
                : `Tell ${p.owner?.name || "the rep"} what to change. The proposal goes back to draft.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">{reviewDialog === "approve" ? "Comment (optional)" : "What needs changing *"}</Label>
            <Textarea rows={4} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog(null)}>
              Cancel
            </Button>
            <Button onClick={review} disabled={busy || (reviewDialog === "reject" && !comment.trim())}>
              {busy && <Loader2 className="size-4 animate-spin mr-2" />}
              {reviewDialog === "approve" ? "Approve" : "Send back"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RecordDecisionDialog
        proposal={p}
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        onDone={(rec) => {
          apply(rec);
          setActivityKey((k) => k + 1);
        }}
      />

      <SendProposalDialog
        proposal={p}
        open={sendOpen}
        onOpenChange={setSendOpen}
        onSent={(res) => {
          apply(res.proposal);
          setActivityKey((k) => k + 1);
        }}
      />

      {/* Delete */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {p.display_number}?</DialogTitle>
            <DialogDescription>This draft has never been sent, so it can be removed. This can't be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={remove} disabled={busy}>
              {busy && <Loader2 className="size-4 animate-spin mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="w-24 shrink-0 text-xs text-muted-foreground pt-0.5">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}
